import { useEffect, useMemo, useState } from "react";
import { Alert, Box, IconButton, LinearProgress, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { useAuth } from "@/context/AuthContext";
import { getUserCompanies } from "@/api/user.service";
import type { Company } from "@/api/company.service";
import { CrudPage } from "@/components/crud/CrudPage";
import {
  ABAS,
  agrupadorDaAba,
  DIMENSAO_LABELS,
  filtroDaLinha,
  getRupturaGeral,
  type RupturaDimensao,
  type RupturaFiltros,
  type RupturaProduto,
  type RupturaResumo,
} from "@/api/ruptura.service";
import { GeralCard, ResumoCard } from "./RupturaCards";
import { SeletorLojas } from "./SeletorLojas";
import { DetalheView } from "./RupturaDetalheView";
import { ProdutosView } from "./RupturaProdutosView";
import { formatData, SEM_FILTRO, useAosPoucos, type AbrirProdutos, type NomeLoja } from "./rupturaComum";
import { CacheRupturaContext, LojasRupturaContext, useRupturaBusca, useRupturaResumo, type CacheRuptura } from "./useRupturaBusca";

// ponytail: mesma navegação de app-mobile/src/app/ruptura.tsx → rupturaProdutos.tsx →
// rupturaProdutoDetalhe.tsx, com os cards no padrão do Flash (HierarchyPanel). As três
// telas ficam montadas e só a atual aparece, para a volta manter abertos/paginação.

export function RupturaPage() {
  // Cache das buscas da tela; o "Recarregar" troca por um vazio.
  const [cache, setCache] = useState<CacheRuptura>(() => new Map());

  return (
    <CacheRupturaContext.Provider value={cache}>
      <RupturaTela onLimparCache={() => setCache(new Map())} />
    </CacheRupturaContext.Provider>
  );
}

function RupturaTela({ onLimparCache }: { onLimparCache: () => void }) {
  const { user } = useAuth();
  const userId = user?.id;
  const clientId = user?.loggedClient?.id;

  const [dimensao, setDimensao] = useState<RupturaDimensao>("loja");
  const [versao, setVersao] = useState(0);
  // Lojas tiradas no seletor "Lojas" (erp_code); todas as outras entram nas buscas.
  const [lojasTiradas, setLojasTiradas] = useState<Set<string>>(new Set());
  // null = ainda carregando: as buscas esperam, porque dependem das lojas.
  const [companies, setCompanies] = useState<Company[] | null>(null);
  // Filtros com que a lista de produtos abriu (null = lista fechada).
  const [produtosDe, setProdutosDe] = useState<RupturaFiltros | null>(null);
  const [detalheDe, setDetalheDe] = useState<RupturaProduto | null>(null);

  useEffect(() => {
    if (!userId || !clientId) return;
    getUserCompanies(userId)
      .then((data) => setCompanies(data.filter((c) => c.client_id === clientId)))
      .catch(() => setCompanies([]));
  }, [userId, clientId]);

  // O nome da loja não é gravado nas tabelas de ruptura: resolve-se pelo
  // cod_empresa (erp_code) nas empresas do usuário logado no cliente.
  const nomeLoja = useMemo<NomeLoja>(
    () => (codEmpresa) => (codEmpresa ? ((companies ?? []).find((c) => c.erp_code === codEmpresa)?.name ?? codEmpresa) : "—"),
    [companies],
  );

  const lojas = useMemo(
    () => (companies ?? []).filter((c) => !lojasTiradas.has(c.erp_code)).map((c) => c.erp_code),
    [companies, lojasTiradas],
  );

  // Sem o geral a tela segue, só sem o card do topo.
  const geral = useRupturaBusca(
    clientId && lojas.length > 0 ? `geral:${clientId}:${lojas.join(",")}` : null,
    () => getRupturaGeral(clientId!, lojas),
    "Não foi possível carregar a ruptura.",
  );
  // Na aba Problema o primeiro nível é o responsável; os problemas abrem dentro dele.
  const agrupador = agrupadorDaAba(dimensao);
  const resumo = useRupturaResumo(agrupador, SEM_FILTRO, clientId, lojas);
  const linhas = resumo.dados ?? [];
  const cards = useAosPoucos(linhas, `${versao}-${dimensao}`);
  // Espera os dois: o card geral não aparece depois, empurrando os demais.
  const loading = companies === null || geral.loading || resumo.loading;

  if (!clientId) {
    return (
      <CrudPage title="Gestão de Ruptura">
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          Selecione um cliente para ver a ruptura.
        </Typography>
      </CrudPage>
    );
  }

  const nomeLinha = (linha: RupturaResumo) => (linha.dimensao === "loja" ? nomeLoja(linha.chave) : linha.rotulo);

  // Atalho de um card: a linha vira mais um filtro da lista.
  const abrirProdutos: AbrirProdutos = (linha, filtros) => setProdutosDe({ ...filtros, ...filtroDaLinha(linha) });

  // Trocar as lojas muda todos os números: fecha a lista de produtos aberta.
  const trocarLojas = (tiradas: Set<string>) => {
    setLojasTiradas(tiradas);
    setProdutosDe(null);
    setDetalheDe(null);
  };

  const recarregar = () => {
    onLimparCache();
    setProdutosDe(null);
    setDetalheDe(null);
    setVersao((v) => v + 1);
  };

  return (
    <LojasRupturaContext.Provider value={lojas}>
      <CrudPage
        title="Gestão de Ruptura"
        subtitle={`Apurado em ${formatData(geral.dados?.data_apuracao)}`}
        headerActions={
          <Tooltip title="Recarregar">
            <IconButton size="small" onClick={recarregar}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      >
        <Box sx={{ display: produtosDe ? "none" : "block" }}>
          <Stack gap={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Tabs value={dimensao} onChange={(_, v) => setDimensao(v)} variant="scrollable" scrollButtons="auto" sx={{ flex: 1, minWidth: 0 }}>
                {ABAS.map((d) => (
                  <Tab key={d} value={d} label={DIMENSAO_LABELS[d]} />
                ))}
              </Tabs>
              <SeletorLojas lojas={companies ?? []} desmarcadas={lojasTiradas} onChange={trocarLojas} />
            </Stack>

            {resumo.erro && <Alert severity="error">{resumo.erro}</Alert>}
            {loading ? (
              <LinearProgress />
            ) : linhas.length === 0 && !resumo.erro ? (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
                {lojas.length === 0 ? "Nenhuma loja selecionada." : "Nenhum dado encontrado para esta dimensão."}
              </Typography>
            ) : (
              <Stack gap={1}>
                {geral.dados && geral.dados.qtd_total > 0 && (
                  <GeralCard
                    key={`${versao}-${dimensao}`}
                    nome={user?.loggedClient?.name ?? "Total"}
                    geral={geral.dados}
                    valorRisco={linhas.reduce((soma, l) => soma + l.valor_risco, 0)}
                    aba={agrupador}
                    clientId={clientId}
                    nomeLoja={nomeLoja}
                    onVerProdutos={abrirProdutos}
                    onVerTodos={() => setProdutosDe(SEM_FILTRO)}
                  />
                )}
                {cards.visiveis.map((linha) => (
                  <ResumoCard
                    key={`${versao}-${dimensao}-${linha.chave}`}
                    item={linha}
                    nome={nomeLinha(linha)}
                    aba={agrupador}
                    clientId={clientId}
                    nomeLoja={nomeLoja}
                    onVerProdutos={abrirProdutos}
                  />
                ))}
                {cards.temMais && <Box ref={cards.fimRef} sx={{ height: 1 }} />}
              </Stack>
            )}
          </Stack>
        </Box>

        {produtosDe && (
          <Box sx={{ display: detalheDe ? "none" : "block" }}>
            <ProdutosView
              key={JSON.stringify(produtosDe)}
              filtrosIniciais={produtosDe}
              clientId={clientId}
              nomeLoja={nomeLoja}
              onVoltar={() => setProdutosDe(null)}
              onVerDetalhe={setDetalheDe}
            />
          </Box>
        )}

        {produtosDe && detalheDe && (
          <DetalheView
            key={`${detalheDe.cod_empresa}-${detalheDe.cod_produto}`}
            produto={detalheDe}
            clientId={clientId}
            nomeLoja={nomeLoja}
            onVoltar={() => setDetalheDe(null)}
          />
        )}
      </CrudPage>
    </LojasRupturaContext.Provider>
  );
}
